import axios from "axios";
import Cookie from "universal-cookie";
import { getCookie, getTimestamp } from "./utils/utils";
import {
  User,
  GPTResponse,
  Member,
  AnsweredQuestions,
  OrgQuestions,
  FeedbackUpdate,
  JobDescription,
  CheckoutSession,
  OrgQuestion,
  SessionQuestion,
  OrganizationInfo,
  Cohort,
  DBQuestions,
  MemberInfo,
  MemberReportInfo,
  InterviewObject,
  AssessmentInfo,
} from "@/utils/types";
import { getInfo, removeInfo } from "./components/signup";

let host = 'https://devdifferencefuncs.azurewebsites.net/api';
//let host = 'http://localhost:7071/api';
//let host = "https://devdifferencefuncs-staging.azurewebsites.net/api";

export async function getTokenOrRefresh(): Promise<{
  authToken: string | null;
  region?: string;
  error?: any;
}> {
  try {
    const speechToken = getCookie("speech-token");
    if (speechToken === null) {
      const res = await axios.get(`${host}/get-speech-token`);
      const token = res.data.token;
      const region = res.data.region;
      const cookie = new Cookie();
      cookie.set("speech-token", region + ":" + token, {
        maxAge: 540,
        path: "/",
      });
      return { authToken: token, region: region };
    } else {
      const idx = speechToken.indexOf(":");
      return {
        authToken: speechToken.slice(idx + 1),
        region: speechToken.slice(0, idx),
      };
    }
  } catch (err: any) {
    console.error("Error in getTokenOrRefresh:", err);
    return { authToken: null, error: err.response?.data || err.message };
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/* outdated and unused */
export async function getGPTResponse(
  promptArr: GPTResponse[],
  showError: (show: boolean) => void
): Promise<string> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ prompt: promptArr });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  let res = await axios.post(`${host}/get-gpt-response`, data, config);
  while (res.status !== 200) {
    showError(true);
    await sleep(3000);
    showError(false);
    res = await axios.post(`${host}/get-gpt-response`, data, config);
  }

  return res.data.message;
}

export async function tt(): Promise<void> {
  axios.get(`http://localhost:7071/api/update-criteria`);
}

export async function getGPTResponseServer(
  promptComp: any,
  showError: (show: boolean) => void
): Promise<any> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ promptComponents: promptComp });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  let res = await axios.post(`${host}/get-gpt-response-server`, data, config);
  while (res.status !== 200) {
    showError(true);
    await sleep(3000);
    showError(false);
    res = await axios.post(`${host}/get-gpt-response-server`, data, config);
  }

  return promptComp["gptCallType"] === "feedback" ? res.data : res.data.message;
}

// {spectype:[]}
export async function getQuestionsDatabase(
  role: string,
  type: string
): Promise<DBQuestions[]> {
  const res = await axios.get(
    `${host}/get-questions?type=${type}&role=${role}`
  );
  return res.data.message;
}

export async function getUser(
  email: string,
  name: string,
  email_verified: boolean
): Promise<User | null> {
  try {
    const bearerToken = getCookie("oidToken");
    const savedOrgInfo = getInfo("orgInfo");
    const completeActivation = getInfo("completeActivation");
    if (savedOrgInfo) {
      removeInfo("orgInfo");
    } else if (completeActivation) {
      removeInfo("completeActivation");
    }
    const data = JSON.stringify({
      email: email,
      name: name,
      email_verified: email_verified,
      timestamp: getTimestamp(),
      addToOrg: savedOrgInfo ?? null,
      activateTempAccount: completeActivation || null,
    });
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    };
    const res = await axios.post(`${host}/get-user`, data, config);
    return res.data.user && Object.keys(res.data.user).length > 0
      ? res.data.user
      : null;
  } catch (err: any) {
    console.error("Error in getUser:", err);
    return null;
  }
}

export async function adminGetMembers(): Promise<Cohort[] | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({});
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/get-members-admin`, data, config);
  return res.data.members;
}

export async function adminGetMembersReportInfo(members: string[]): Promise<MemberReportInfo[]> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ members });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/get-members-report-info-admin`, data, config);
  return res.data.membersReportInfo;
}

export async function adminInviteMembers(
  members: Member[]
): Promise<Cohort[] | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ members: members });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/invite-members-admin`, data, config);
  return res.data.members;
}

export async function adminRemindMembers(members: Member[]): Promise<number> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ members: members });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/resend-invite-admin`, data, config);
  return res.status;
}

export async function adminDeleteUnusedMembers(
  members: Member[]
): Promise<Cohort[] | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ members: members });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/delete-members-admin`, data, config);
  return res.data.members;
}

export async function adminUpdateMembers(
  newList: Member[]
): Promise<Cohort[] | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ members: newList });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-members-admin`, data, config);
  return res.data.members;
}

export async function adminGetOrgInfo(): Promise<OrganizationInfo | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({});
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(
    `${host}/get-organization-info-admin`,
    data,
    config
  );
  return res.data.organizationInfo;
}

export async function adminGetMemberInfo(
  email: string
): Promise<MemberInfo | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ memberEmail: email });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/get-member-info-admin`, data, config);
  return res.data.memberInfo;
}

export async function getOrgQuestions(): Promise<OrgQuestions | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({});
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/get-org-questions`, data, config);
  return res.data.orgQuestions;
}

export async function adminModifyOrgQuestions(
  type: string,
  qArr: any
): Promise<{ [key: string]: OrgQuestion[] } | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ type, qInfoArray: qArr });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/modify-questions-admin`, data, config);
  return res.data.orgQuestions;
}

export async function uploadUserResume(
  text: string,
  file: string
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    resumetext: text,
    resumename: file,
    type: "resume",
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function uploadPersonalInfo(
  name: string,
  role?: string
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    name,
    role: role ? role : "",
    type: "personalInfo",
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function checkoutWithStripe(
  productId: string
): Promise<CheckoutSession | null> {
  const data = JSON.stringify({ PRICE_ID: productId });
  const bearerToken = getCookie("oidToken");
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/stripe-checkout-session`, data, config);
  return res.data;
}

export async function registerPaidUser(id: string): Promise<User | null> {
  const data = JSON.stringify({ sessionId: id });
  const bearerToken = getCookie("oidToken");
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/register-paid-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function GetPortalLink(): Promise<string | null> {
  const data = JSON.stringify({});
  const bearerToken = getCookie("oidToken");
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/create-portal-session`, data, config);
  return res.data.redirectUrl;
}

export async function updateUserVerification(): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ type: "email_verification" });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateAudio(audio: boolean): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ audioEnabled: audio, type: "audio" });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateFeedbackAccessibility(
  accessibility: boolean,
  question: string,
  index: number
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    accessibility,
    question,
    index,
    type: "feedbackAccessibility",
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateUserResumeQuestions(
  questions: string[]
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    resumeQuestions: questions,
    type: "resumeQuestions",
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function resendVerificationEmail(email: string): Promise<number> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ email: email });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/resend-verification`, data, config);
  return res.status;
}

export async function updateIntakeForm(info: any): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ info: info, type: "intakeForm" });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateJobDescriptions(
  info: JobDescription
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    JobDescriptions: info,
    type: "JobDescriptions",
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateUserAnsweredQuestions(
  question: string,
  qInfo: FeedbackUpdate
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    type: "answeredQuestionUpdate",
    qInfo: qInfo,
    question: question,
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateTempUser(
  email: string,
  question: string,
  qInfo: InterviewObject
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    qInfo: qInfo,
    question: question,
    email: email,
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-temp-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function updateUserSavedSessions(
  session: SessionQuestion[]
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({
    type: "saveSession",
    session,
    timestamp: getTimestamp(),
  });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function removeUserSavedSessions(
  timestamp: string
): Promise<User | null> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ type: "removeSavedSession", timestamp });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  const res = await axios.post(`${host}/update-user`, data, config);
  return res.data.user && Object.keys(res.data.user).length > 0
    ? res.data.user
    : null;
}

export async function validatePreAssessmentLink(token: string, preAssessment: boolean): Promise<AssessmentInfo | null> {
  try {
    const bearerToken = getCookie("oidToken");
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    };
    const res = await axios.get(
      `${host}/validate-magic-link?token=${token}&preAssessment=${preAssessment}`,
      config
    );
    return res.data?.assessmentDetails||null;
  } catch (err: any) {
    console.error("Error in validatePreAssessmentLink:", err);
    return null;
  }
}

export async function getGPTResponseAssessment(
  userResponse: string, currentQuestion:number, timer: number, token: string,
  showError?: (show: boolean) => void
): Promise<{response:string, end: boolean}> {
  const bearerToken = getCookie("oidToken");
  const data = JSON.stringify({ userResponse, timer, token, currentQuestion });
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${bearerToken}`,
    },
  };
  let res = await axios.post(`${host}/get-gpt-response-assessment`, data, config);
  while (res.status !== 200) {
    if(showError) {
      showError(true);
    }
      await sleep(3000);
    if(showError) {
      showError(false);
    }
    res = await axios.post(`${host}/get-gpt-response-assessment`, data, config);
  }

  return {response: res.data.response, end: res.data.endQuestion};
}


export async function UpdateAssessmentNextQuestion(token: string, prevQ:number): Promise<void> {
  try {
    const bearerToken = getCookie("oidToken");
     const data = JSON.stringify({ token: token, question:prevQ });
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    };
    const res = await axios.post(
      `${host}/update-assessment-question`, data,
      config
    );
  } catch (err: any) {
    console.error("Error in validatePreAssessmentLink:", err);
  }
}

export async function SubmitAssessment(token: string, question: number, session:SessionQuestion[]): Promise<AssessmentInfo | null> {
  try {
    const bearerToken = getCookie("oidToken");
     const data = JSON.stringify({ token: token, question:question, session });
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    };
    const res = await axios.post(
      `${host}/submit-assessment`, data,
      config
    );
    return res.data?.assessmentDetails||null;
  } catch (err: any) {
    console.error("Error in validatePreAssessmentLink:", err);
    return null;
  }
}